import {parseQueryProps, parseTagProps,} from "../utils/propsParser";
import {PropsParser} from "@webng/validations";
import {SharedThemeConfiguration} from "@webng-types/embedjs";
import globalSettings from "./globalSettings";
import ReactDOM from "react-dom/client";
import {notifyError} from "../analytics/notifyError";
import {ReactNode} from "react";
import {domContentLoaded} from "../utils/domContentLoaded";
import {parsePartialSharedThemeConfiguration} from "@webng-types/embedjs/shared";
import {tik4DebugLog} from "./debugLog";

function readMetaTags(prefix: string) {
  const metaTags = document.querySelectorAll(`meta[property^="${prefix}:"]`);
  const metaValues: Record<string, string> = {};

  metaTags.forEach(tag => {
    const property = tag.getAttribute('property');
    if (property) {
      const propertyName = property.substring(prefix.length + 1); // +1 for the colon
      const content = tag.getAttribute('content');
      if (content) {
        metaValues[propertyName] = content;
      }
    }
  });

  return metaValues
}

function readElementAttributes(element: HTMLElement) {
  const attrs: Record<string, string> = {};
  for (let attr of element.attributes) {
    attrs[attr.name] = attr.value;
  }
  return attrs;
}

// Unique per element instance across all subclasses, so debug logs from
// several widgets on the same page can be told apart.
let debugInstanceCounter = 0;

export abstract class AbstractLiveblogElement<Configuration, TagProps, QueryProps, T> extends HTMLElement {
  private readonly wrapper: HTMLElement;
  private tagPropsParser: PropsParser<TagProps>;
  private queryPropsParser: PropsParser<QueryProps>;
  private props?: {tagProps: TagProps, queryProps: QueryProps, overrideSharedConfiguration: Partial<SharedThemeConfiguration>, overrideConfiguration: Partial<Configuration>};
  private isRendered = false;
  private rerenderAfterHydrate = false
  protected liveblogComponent: T|undefined;
  private root: ReactDOM.Root|undefined
  // Public so events fired from inside the React tree can attribute themselves to this
  // instance via debugIdForElement() (see debugLog.ts).
  readonly tik4DebugId: string;

  protected constructor(tagPropsParser: PropsParser<TagProps>, queryPropsParser: PropsParser<QueryProps>) {
    super();
    this.wrapper = this;
    this.tagPropsParser = tagPropsParser;
    this.queryPropsParser = queryPropsParser;
    this.tik4DebugId = `${this.tagName.toLowerCase()}#${++debugInstanceCounter}`;
    this.debugLog("constructor");
  }

  private debugLog(callback: string, ...args: unknown[]) {
    tik4DebugLog(this.tik4DebugId, callback, ...args);
  }

  protected attachComponent(ref: T) {
    this.liveblogComponent = ref;
  }

  connectedCallback() {
    this.debugLog("connectedCallback");
    this.props = this.parseProps();
    if (this.props) {
      if (globalSettings.useHydrate && this.useHydrate(this.props)) {
        this.hydrateWidget(this.wrapper, this.props, (ref) => this.attachComponent(ref));
      } else {
        this.renderWidget(this.wrapper, this.props, (ref) => this.attachComponent(ref));
      }
    }
  }

  disconnectedCallback() {
    this.debugLog("disconnectedCallback");
    this.isRendered = false;
    this.liveblogComponent = undefined;
    this.root?.unmount()
    this.root = undefined
  }

  adoptedCallback() {
    this.debugLog("adoptedCallback");
  }

  attributeChangedCallback(
    name: string,
    oldValue: string | null,
    newValue: string | null
  ) {
    this.debugLog("attributeChangedCallback", {name, oldValue, newValue});
    if (this.isRendered && !this.rerenderAfterHydrate) {
      this.props = this.parseProps();
      this.renderWidget(this.wrapper, this.props, (ref) => this.attachComponent(ref));
    } else {
      this.rerenderAfterHydrate = true
    }
  }

  abstract parseConfigurationProps(metaValues: Record<string, string>, elementAttributes: Record<string, string>): Partial<Configuration>

  parseProps() {
    const metaValues = readMetaTags(this.tagName.toLowerCase());
    const elementAttributes = readElementAttributes(this);

    const tagProps = parseTagProps(this.tagPropsParser, this);
    const queryProps = parseQueryProps(this.queryPropsParser);

    const metaValues_sharedThemeConfiguration = parsePartialSharedThemeConfiguration(metaValues);
    const elementAttributes_sharedThemeConfiguration = parsePartialSharedThemeConfiguration(elementAttributes);

    return {
      tagProps,
      queryProps,
      overrideSharedConfiguration: Object.assign({}, metaValues_sharedThemeConfiguration, elementAttributes_sharedThemeConfiguration),
      overrideConfiguration: this.parseConfigurationProps(metaValues, elementAttributes)
    }
  }

  /**
   * IMPORTANT: the widget mounts into the HOST PAGE's document — no iframe, no shadow root.
   *
   * Third-party integrations depend on this. In particular Viafoura conversations identify the
   * publisher by matching the page's `og:url` against their domain allowlist, and their base
   * script is injected into `document.head`. Isolating this widget in an iframe or a shadow
   * root would silently break Viafoura on every customer site — the widget would render
   * nothing, with no error. Do not change the mount strategy without revisiting that.
   */
  renderWidget(el: Element, props: {tagProps: TagProps, queryProps: QueryProps, overrideSharedConfiguration: Partial<SharedThemeConfiguration>, overrideConfiguration: Partial<Configuration>}, refCallback: (ref: T) => void) {
    if(!this.root) {
      this.root = ReactDOM.createRoot(el, {
        onRecoverableError: (error: unknown, errorInfo: ReactDOM.ErrorInfo) => {
          notifyError(error, {
            digest: errorInfo.digest || "",
            componentStack: errorInfo.componentStack || ""
          })
        }
      })
    }
    this.root.render(this.renderImp(refCallback, props))
    this.isRendered = true;
    this.rerenderAfterHydrate = false;
  }

  hydrateWidget(el: Element, props: {tagProps: TagProps, queryProps: QueryProps, overrideSharedConfiguration: Partial<SharedThemeConfiguration>, overrideConfiguration: Partial<Configuration>}, refCallback: (ref: T) => void) {
    return domContentLoaded(() => {
      const refCallbackWrapper = (ref: T) => {
        if(this.rerenderAfterHydrate) {
          setTimeout(() => {
            this.props = this.parseProps();
            this.renderWidget(this.wrapper, this.props, (ref) => this.attachComponent(ref));
          }, 100)
        } else {
          this.isRendered = true;
        }

        refCallback(ref)
      }

      // only send one hydration error
      let sentHydrationError = false

      this.root = ReactDOM.hydrateRoot(el, this.renderImp(refCallbackWrapper, props), {
        onRecoverableError: (error: unknown, errorInfo: ReactDOM.ErrorInfo) => {
          if(error instanceof Error && error.message.match(/Minified React error #(418|423|425)/)) {
            if(sentHydrationError) {
              return
            } else {
              sentHydrationError = true
            }
          }
          notifyError(error, {
            digest: errorInfo.digest || "",
            componentStack: errorInfo.componentStack || ""
          })
        }
      })
    })
  }

  abstract useHydrate(props: {tagProps: TagProps, queryProps: QueryProps, overrideSharedConfiguration: Partial<SharedThemeConfiguration>, overrideConfiguration: Partial<Configuration>}): boolean

  abstract renderImp(ref: (r: T) => void, props: {tagProps: TagProps, queryProps: QueryProps, overrideSharedConfiguration: Partial<SharedThemeConfiguration>, overrideConfiguration: Partial<Configuration>}): ReactNode
}
